import { getI18n } from 'react-i18next';
const supportCurrencies = [
    'EUR',
    'GBP',
    'USD',
    'MXN',
    'DKK',
    'PLN',
    'CZK',
    'HUF',
    'SEK',
];
export const isCurrency = (value) => supportCurrencies.some((currency) => currency === value);
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const isMoneyLike = (value) => value &&
    typeof value === 'object' &&
    'value' in value &&
    typeof value.value === 'number' &&
    'currency' in value &&
    isCurrency(value.currency);
const operator = (fn) => (money1, moneyOrValue) => ({
    value: fn(money1.value, isMoneyLike(moneyOrValue) ? moneyOrValue.value : moneyOrValue),
    currency: money1.currency,
});
const zero = (currency) => ({
    currency,
    value: 0,
});
const add = operator((v1, v2) => v1 + v2);
const subtract = operator((v1, v2) => v1 - v2);
const divide = operator((v1, v2) => v1 / v2);
const multiply = operator((v1, v2) => v1 * v2);
const isPositive = (money) => money.value > 0;
const isPositiveOrZero = (money) => money.value >= 0;
const isNegative = (money) => money.value < 0;
const isNegativeOrZero = (money) => money.value <= 0;
const min = (...monies) => monies.reduce((money, other) => (money.value < other.value ? money : other));
const max = (...monies) => monies.reduce((money, other) => (money.value > other.value ? money : other));
const format = (money, locales) => money.value.toLocaleString(locales, {
    currency: money.currency,
});
export const literalCurrencyToSymbol = (literalCurrency) => {
    switch (literalCurrency) {
        case 'EUR':
            return '€\u00a0';
        case 'GBP':
            return '£\u00a0';
        case 'USD':
        case 'MXN':
            return '$\u00a0';
        case 'PLN':
            return 'zl\u00a0';
        case 'CZK':
            return 'Kč\u00a0';
        case 'HUF':
            return 'Ft\u00a0';
        case 'SEK':
            return 'kr\u00a0';
        case 'DKK':
            return 'kr.\u00a0';
        default:
            return '';
    }
};
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
const toString = (money, fractionDigits = 2) => {
    var _a;
    const decimalType = (1.1).toLocaleString((_a = getI18n()) === null || _a === void 0 ? void 0 : _a.language).substring(1, 2);
    return (literalCurrencyToSymbol(money.currency) +
        money.value.toFixed(fractionDigits).replace(/[.,]/, decimalType));
};
export class Money {
    constructor(value, currency) {
        this.value = value;
        this.currency = currency;
        if (!isCurrency(currency))
            throw new Error(`IllegalArgumentException: value ${currency} not supported`);
        if (!(typeof value === 'number'))
            throw new Error(`IllegalArgumentException: value ${value} not supported`);
    }
    add(other) {
        return Money.add(this, other);
    }
    subtract(other) {
        return Money.subtract(this, other);
    }
    divide(divisor) {
        return Money.divide(this, divisor);
    }
    multiply(multiplier) {
        return Money.multiply(this, multiplier);
    }
    isPositive() {
        return Money.isPositive(this);
    }
    isPositiveOrZero() {
        return Money.isPositiveOrZero(this);
    }
    isNegative() {
        return Money.isNegative(this);
    }
    isNegativeOrZero() {
        return Money.isNegativeOrZero(this);
    }
    format(locales) {
        return Money.format(this, locales);
    }
    toString() {
        return Money.toString(this);
    }
    static of(value, currency) {
        return new Money(value, currency);
    }
    static from({ value, currency }) {
        return new Money(value, currency);
    }
    static zero(currency) {
        return Money.from(zero(currency));
    }
    static add(money1, money2) {
        return Money.from(add(money1, money2));
    }
    static subtract(money1, money2) {
        return Money.from(subtract(money1, money2));
    }
    static divide(money, divisor) {
        return Money.from(divide(money, divisor));
    }
    static multiply(money, multiplier) {
        return Money.from(multiply(money, multiplier));
    }
    static isPositive(money) {
        return isPositive(money);
    }
    static isPositiveOrZero(money) {
        return isPositiveOrZero(money);
    }
    static isNegative(money) {
        return isNegative(money);
    }
    static isNegativeOrZero(money) {
        return isNegativeOrZero(money);
    }
    static min(...monies) {
        return Money.from(min(...monies));
    }
    static max(...monies) {
        return Money.from(max(...monies));
    }
    static format(money, locales) {
        return format(money, locales);
    }
    static toString(money, fractionDigits) {
        return toString(money, fractionDigits);
    }
}
